import printJS from 'print-js';
import { IS_CLOUD_PRINTER_CONFIGURED, createCloudPrintJob } from '@shared/api/cloudPrinter';
import { readSelectedPrinter } from './printerSelection';

/**
 * Единая точка печати этикеток: если оператор выбрал принтер — задание уходит на него
 * через облачную печать; если принтер не выбран, доступных принтеров нет или отправка
 * сорвалась по любой причине — печатаем как раньше, диалогом браузера (print-js).
 * Оператор при любом раскладе получает этикетку.
 *
 * Тостов и звуков здесь нет намеренно: печать молчалива, состояние принтера видно
 * в строке выбора в подвале sidebar.
 *
 * Живёт в `shared`, а не в виджете, потому что вызывается из `features/*`, которым
 * импорт из `widgets/*` запрещён границами FSD.
 *
 * Выбор читаем из localStorage: маршрутизация нужна и в обычных async-функциях API,
 * где хуков нет. Запись синхронизирует с бэком строка выбора принтера в sidebar —
 * она чистит её, если привязки на бэке уже нет. Расхождение не опасно: устаревший выбор
 * даст ошибку отправки и тот же откат на локальную печать.
 */

/**
 * Куда в итоге ушла этикетка:
 * - `cloud` — принята облачным принтером;
 * - `fallback` — принтер выбран, но отправка сорвалась → напечатали в браузере;
 * - `local` — принтер не выбран, сразу браузер.
 */
export type PrintOutcome = 'cloud' | 'fallback' | 'local';

/** Параметры печати. Оба поля опциональны: у большинства этикеток нет ни размера, ни модалки. */
export interface PrintOptions {
  /**
   * Размер этикетки (`50x80`) — уходит транзитом в десктоп-приложение облачной печати,
   * оно по нему выбирает конкретный принтер. Не задан — гейтвей подставит свой дефолт.
   */
  labelSize?: string;
  /**
   * Показывать ли модалку загрузки print-js при ЛОКАЛЬНОЙ печати. На облачную не влияет.
   * Нужна там, где она была исторически (печать этикеток мешков и посылок), — чтобы
   * поведение без выбранного принтера осталось прежним.
   */
  showModal?: boolean;
}

/** Локальная печать — прежнее поведение: PDF открывается в диалоге печати браузера. */
export function printPdfLocally(pdf: Blob, options?: PrintOptions): void {
  const url = URL.createObjectURL(new Blob([pdf], { type: 'application/pdf' }));
  printJS({ printable: url, type: 'pdf', showModal: options?.showModal });
  // print-js читает URL асинхронно (грузит во фрейм) — освобождаем отложенно.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Напечатать готовый PDF с маршрутизацией: выбран принтер — в облако, иначе диалогом браузера. */
export async function printPdfRouted(pdf: Blob, options?: PrintOptions): Promise<PrintOutcome> {
  const selected = IS_CLOUD_PRINTER_CONFIGURED ? readSelectedPrinter() : null;

  if (selected) {
    try {
      await createCloudPrintJob(pdf, options?.labelSize);
      return 'cloud';
    } catch {
      // Любая ошибка отправки — не тупик: печатаем локально.
      printPdfLocally(pdf, options);
      return 'fallback';
    }
  }

  printPdfLocally(pdf, options);
  return 'local';
}

/** То же, но для этикеток, которые бэк отдаёт строкой base64 (например, почтовая этикетка). */
export async function printBase64PdfRouted(base64Pdf: string, options?: PrintOptions): Promise<PrintOutcome> {
  const normalized = base64Pdf.replace(/^data:.*;base64,/, '').replace(/\s/g, '').trim();
  const bytes = atob(normalized);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return printPdfRouted(new Blob([arr], { type: 'application/pdf' }), options);
}
